/**
 * QuestionCard.tsx — One question at a time in the center column of Answer Lane
 *
 * W21: Primary Answer Surface
 *
 * Handles response types:
 *   select / multi-select — radio/checkbox tiles
 *   currency — money input with $ prefix and thousands separator
 *   date — date picker
 *   text-short — single-line text
 *   text-long — textarea (4 rows)
 *
 * Coach helper sentence below input.
 * Evidence section (collapsible, EvidenceTabs).
 * Previous answers strip at bottom (collapsible, PreviousAnswersStrip).
 * Autosave indicator.
 *
 * Brand: black #0B0B0F, red #DC2626, soft white #FAFAF7
 * No emojis. No exclamation points.
 */

import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import type { Question, SavePayload } from "@/hooks/useAnswerLane";
import EvidenceTabs, { type EvidenceValue } from "./EvidenceTabs";
import PreviousAnswersStrip from "./PreviousAnswersStrip";

// Coach hints by CP (mentor voice, not punitive)
const CP_COACH_HINTS: Record<number, string> = {
  1:  "Confirm this is a real, funded project — not just exploratory interest.",
  2:  "Look for a recognized need the buyer has expressed in their own words.",
  3:  "Budget is confirmed when there is a named source and a validated line item.",
  4:  "Timeline is real only if there is both a decision window and an implementation window.",
  5:  "You need direct access to the person who can say yes — not just a sponsor.",
  6:  "Technical and financial sign-off are separate gates — confirm both are clear.",
  7:  "Map the org structure before you assume who controls the final decision.",
  8:  "Know exactly where you stand competitively — and where the gaps are.",
  9:  "Compliance barriers discovered late cost deals. Surface them early.",
  10: "Legal, SLAs, and contract terms are closing gates — not afterthoughts.",
};

interface Props {
  question: Question;
  allQuestionsInCp: Question[];
  cpNumber: number;
  onSave: (payload: SavePayload, opts?: { immediate?: boolean }) => Promise<void>;
  savingKey: string | null;
  lastSavedAt: Date | null;
  onAdvance: () => void;
  /** Called by keyboard handlers above */
  onPrev?: () => void;
  /** Called by keyboard handlers above */
  onNext?: () => void;
}

export interface QuestionCardHandle {
  triggerSave: (opts?: { advance?: boolean }) => void;
}

function formatCurrency(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return "";
  return parseInt(digits, 10).toLocaleString("en-US");
}

function parseCurrency(formatted: string): string {
  return formatted.replace(/,/g, "");
}

const QuestionCard = forwardRef<QuestionCardHandle, Props>(function QuestionCard(
  {
    question,
    allQuestionsInCp,
    cpNumber,
    onSave,
    savingKey,
    lastSavedAt,
    onAdvance,
    onPrev,
    onNext,
  },
  ref,
) {
  const isSaving = savingKey === question.question_key;
  const [value, setValue] = useState<string>(question.current_answer?.answer_value ?? "");
  const [multiValues, setMultiValues] = useState<Set<string>>(
    question.current_answer?.answer_value
      ? new Set(question.current_answer.answer_value.split(",").map((v) => v.trim()))
      : new Set(),
  );
  const [notes, setNotes] = useState<string>(question.current_answer?.answer_notes ?? "");
  const [evidence, setEvidence] = useState<EvidenceValue>({
    evidence_url: question.current_answer?.evidence_url,
    evidence_text: question.current_answer?.evidence_text,
  });
  const [celebrationText, setCelebrationText] = useState<string | null>(null);

  // Reset local state when question changes
  useEffect(() => {
    setValue(question.current_answer?.answer_value ?? "");
    setMultiValues(
      question.current_answer?.answer_value
        ? new Set(question.current_answer.answer_value.split(",").map((v) => v.trim()))
        : new Set(),
    );
    setNotes(question.current_answer?.answer_notes ?? "");
    setEvidence({
      evidence_url: question.current_answer?.evidence_url,
      evidence_text: question.current_answer?.evidence_text,
    });
  }, [question.question_key]);

  const buildPayload = useCallback((): SavePayload => {
    const isMulti = question.response_type === "multi-select";
    const answerValue = isMulti
      ? Array.from(multiValues).join(", ")
      : question.response_type === "currency"
        ? parseCurrency(value)
        : value;
    return {
      answer_value: answerValue || null,
      answer_notes: notes || undefined,
      is_evidence: !!(evidence.evidence_url || evidence.evidence_text || evidence.evidence_file_url),
      evidence_url: evidence.evidence_url || undefined,
      evidence_text: evidence.evidence_text || undefined,
      evidence_file_url: evidence.evidence_file_url || undefined,
    };
  }, [question.response_type, value, multiValues, notes, evidence]);

  const handleSaveAndAdvance = useCallback(async () => {
    await onSave(buildPayload(), { immediate: true });
    onAdvance();
  }, [onSave, buildPayload, onAdvance]);

  const handleSaveOnly = useCallback(async () => {
    await onSave(buildPayload(), { immediate: true });
  }, [onSave, buildPayload]);

  useImperativeHandle(ref, () => ({
    triggerSave: (opts) => {
      if (opts?.advance) {
        handleSaveAndAdvance();
      } else {
        handleSaveOnly();
      }
    },
  }));

  const responseType = question.response_type ?? "text-short";
  const options = question.options ?? [];
  const coachHint = CP_COACH_HINTS[cpNumber] ?? "Answer with the most current information you have from the buyer.";

  // Seconds since last save
  const secondsSince = lastSavedAt
    ? Math.round((Date.now() - lastSavedAt.getTime()) / 1000)
    : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        height: "100%",
        overflowY: "auto",
        padding: "24px 28px 32px",
        background: "#FFFFFF",
      }}
    >
      {/* CP badge */}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.16em",
          color: "#9CA3AF",
          marginBottom: 10,
        }}
      >
        Checkpoint {cpNumber}
      </div>

      {/* Question prompt */}
      <h2
        style={{
          fontSize: 17,
          fontWeight: 700,
          color: "#0B0B0F",
          lineHeight: 1.4,
          marginBottom: 20,
          margin: "0 0 20px",
        }}
      >
        {question.question_text}
      </h2>

      {/* Answer input by response type */}
      <div style={{ flex: 1 }}>
        {(responseType === "select") && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {options.map((opt) => {
              const optKey = typeof opt === "string" ? opt : opt.key;
              const optLabel = typeof opt === "string" ? opt : opt.label;
              const selected = value === optKey;
              return (
                <button
                  key={optKey}
                  type="button"
                  onClick={() => {
                    const newVal = selected ? "" : optKey;
                    setValue(newVal);
                    onSave({
                      ...buildPayload(),
                      answer_value: newVal || null,
                    });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 14px",
                    border: `1px solid ${selected ? "#0B0B0F" : "#E5E5E7"}`,
                    borderRadius: 8,
                    background: selected ? "#0B0B0F" : "#FFFFFF",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.12s",
                  }}
                  aria-pressed={selected}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: `2px solid ${selected ? "#FFFFFF" : "#9CA3AF"}`,
                      background: selected ? "#FFFFFF" : "transparent",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: selected ? 600 : 400,
                      color: selected ? "#FFFFFF" : "#0B0B0F",
                    }}
                  >
                    {optLabel}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {responseType === "multi-select" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {options.map((opt) => {
              const optKey = typeof opt === "string" ? opt : opt.key;
              const optLabel = typeof opt === "string" ? opt : opt.label;
              const checked = multiValues.has(optKey);
              return (
                <button
                  key={optKey}
                  type="button"
                  onClick={() => {
                    const next = new Set(multiValues);
                    if (checked) next.delete(optKey);
                    else next.add(optKey);
                    setMultiValues(next);
                    onSave({
                      ...buildPayload(),
                      answer_value: Array.from(next).join(", ") || null,
                    });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 14px",
                    border: `1px solid ${checked ? "#0B0B0F" : "#E5E5E7"}`,
                    borderRadius: 8,
                    background: checked ? "#0B0B0F" : "#FFFFFF",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.12s",
                  }}
                  aria-pressed={checked}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      border: `2px solid ${checked ? "#FFFFFF" : "#9CA3AF"}`,
                      background: checked ? "#FFFFFF" : "transparent",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: checked ? 600 : 400,
                      color: checked ? "#FFFFFF" : "#0B0B0F",
                    }}
                  >
                    {optLabel}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {responseType === "currency" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #C9C9CC",
              borderRadius: 8,
              overflow: "hidden",
              background: "#FFFFFF",
            }}
          >
            <span
              style={{
                padding: "11px 14px",
                fontSize: 16,
                fontWeight: 600,
                color: "#525258",
                background: "#F9FAFB",
                borderRight: "1px solid #C9C9CC",
              }}
            >
              $
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={formatCurrency(value)}
              placeholder="0"
              onChange={(e) => {
                const formatted = formatCurrency(e.target.value);
                setValue(formatted);
                onSave({ ...buildPayload(), answer_value: parseCurrency(formatted) || null });
              }}
              style={{
                flex: 1,
                padding: "11px 14px",
                fontSize: 16,
                fontWeight: 600,
                color: "#0B0B0F",
                border: "none",
                outline: "none",
                fontFamily: "inherit",
              }}
              aria-label="Currency answer"
            />
          </div>
        )}

        {responseType === "date" && (
          <input
            type="date"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              onSave({ ...buildPayload(), answer_value: e.target.value || null });
            }}
            style={{
              padding: "11px 14px",
              border: "1px solid #C9C9CC",
              borderRadius: 8,
              fontSize: 14,
              color: "#0B0B0F",
              outline: "none",
              fontFamily: "inherit",
              width: "100%",
              boxSizing: "border-box",
            }}
            aria-label="Date answer"
          />
        )}

        {responseType === "text-short" && (
          <input
            type="text"
            value={value}
            placeholder="Your answer..."
            onChange={(e) => {
              setValue(e.target.value);
              onSave({ ...buildPayload(), answer_value: e.target.value || null });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveAndAdvance();
              }
            }}
            style={{
              width: "100%",
              padding: "11px 14px",
              border: "1px solid #C9C9CC",
              borderRadius: 8,
              fontSize: 14,
              color: "#0B0B0F",
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
            aria-label="Short text answer"
          />
        )}

        {responseType === "text-long" && (
          <textarea
            rows={4}
            value={value}
            placeholder="Your answer..."
            onChange={(e) => {
              setValue(e.target.value);
              onSave({ ...buildPayload(), answer_value: e.target.value || null });
            }}
            style={{
              width: "100%",
              padding: "11px 14px",
              border: "1px solid #C9C9CC",
              borderRadius: 8,
              fontSize: 14,
              color: "#0B0B0F",
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
              resize: "vertical",
              lineHeight: 1.5,
            }}
            aria-label="Long text answer"
          />
        )}

        {/* Coach helper */}
        <p
          style={{
            fontSize: 12,
            color: "#6B6B70",
            marginTop: 8,
            fontStyle: "italic",
            lineHeight: 1.5,
          }}
        >
          {coachHint}
        </p>

        {/* Save indicator */}
        <div style={{ marginTop: 6, minHeight: 18 }}>
          {isSaving ? (
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>Saving...</span>
          ) : secondsSince !== null && secondsSince < 60 ? (
            <span style={{ fontSize: 11, color: "#16A34A" }}>
              Saved {secondsSince}s ago
            </span>
          ) : secondsSince !== null ? (
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>
              Saved {Math.round(secondsSince / 60)}m ago
            </span>
          ) : null}
        </div>
      </div>

      {/* Evidence section */}
      <EvidenceTabs
        value={evidence}
        onChange={(ev) => {
          setEvidence(ev);
          onSave({ ...buildPayload(), ...ev });
        }}
        defaultOpen={false}
      />

      {/* Previous answers strip */}
      <PreviousAnswersStrip
        cpNumber={cpNumber}
        questions={allQuestionsInCp}
        activeKey={question.question_key}
      />

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 20,
          justifyContent: "flex-end",
        }}
      >
        <button
          type="button"
          onClick={handleSaveOnly}
          disabled={isSaving}
          style={{
            padding: "9px 18px",
            borderRadius: 7,
            fontSize: 13,
            fontWeight: 600,
            cursor: isSaving ? "default" : "pointer",
            background: "#FFFFFF",
            color: "#0B0B0F",
            border: "1px solid #C9C9CC",
            opacity: isSaving ? 0.5 : 1,
          }}
        >
          Save (Cmd+S)
        </button>
        <button
          type="button"
          onClick={handleSaveAndAdvance}
          disabled={isSaving}
          style={{
            padding: "9px 18px",
            borderRadius: 7,
            fontSize: 13,
            fontWeight: 700,
            cursor: isSaving ? "default" : "pointer",
            background: "#0B0B0F",
            color: "#FFFFFF",
            border: "none",
            opacity: isSaving ? 0.5 : 1,
          }}
        >
          Save and continue (Enter)
        </button>
      </div>

      {/* CP complete celebration */}
      {celebrationText && (
        <div
          style={{
            marginTop: 16,
            padding: "12px 16px",
            borderRadius: 8,
            background: "#F0FDF4",
            border: "1px solid #16A34A",
            fontSize: 13,
            color: "#166534",
            fontWeight: 500,
            textAlign: "center",
          }}
          role="status"
        >
          {celebrationText}
        </div>
      )}
    </div>
  );
});

export default QuestionCard;
